'use client';

import { planLabel } from '../../lib/pricing';
import { useState, useRef, useEffect, useId } from 'react';
import Link from 'next/link';
import { useAuth } from './AuthProvider';
import AccountAvatar from './AccountAvatar';
import LibraryNavigation from './LibraryNavigation';
import { accountIdentity } from '@/lib/account-identity';
import { useSubscription } from './SubscriptionProvider';
import type { User } from '../../lib/supabase';

interface UserMenuProps {
  user: User;
}

export function UserMenu({ user }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const { signOut, accountProfile } = useAuth();
  const { tier } = useSubscription();

  const identity = accountIdentity(user, accountProfile);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && menuRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-8 h-8 rounded-full overflow-hidden border-2 border-transparent hover:border-primary/20 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
        aria-label="User menu"
      >
        <AccountAvatar image={identity.image} initials={identity.initials} />
      </button>

      {isOpen && (
        <div id={menuId} className="absolute right-0 mt-2 max-h-[calc(100dvh-5rem)] overflow-y-auto w-56 bg-card rounded-xl shadow-lg border border-border py-1 z-50 animate-in fade-in zoom-in-95 duration-150">
          <div className="px-4 py-2 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-foreground truncate flex-1">
                {identity.name}
              </div>
              {tier !== 'free' && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  tier === 'power' || tier === 'pro' ? 'bg-primary/10 text-primary' : 'bg-blue-50 text-blue-600'
                }`}>
                  {planLabel(tier)}
                </span>
              )}
            </div>
            <div className="text-xs text-muted truncate">
              {user.email}
            </div>
          </div>

          <LibraryNavigation onNavigate={() => setIsOpen(false)} />
          <Link
            href="/account"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted-bg transition-colors"
          >
            <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
            </svg>
            Account
          </Link>

          <Link
            href="/#pricing"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted-bg transition-colors"
          >
            <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
            </svg>
            Pricing
          </Link>

          <div className="border-t border-border mt-1 pt-1">
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted-bg transition-colors"
            >
              <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
